export interface SyncNotification {
  id: number;
  category: string;
  severity: 'info' | 'warning' | 'error';
  title: string;
  body: string | null;
  aggregate: string;
  aggregate_id: string;
  status: 'unread' | 'read' | 'resolved' | 'dismissed';
  attempts: number;
  correlation_id: string | null;
  action_type: string | null;
  /**
   * 状態の不一致 rows only — whether the nightly reconcile left a 不一致明細CSV beside this notice.
   *
   * A FLAG, not the path: the file is fetched through the endpoint, which re-checks the site scope. False on
   * every other category, and false on a drift row whose file could not be written.
   */
  has_drift_csv?: boolean;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  /**
   * BD 8.0–10.0 — which clinic, and so which site, this notice belongs to. Stamped when the notice is
   * written, not derived when it is read.
   *
   * NULL IS A REAL ANSWER, not missing data: the notice belongs to no one clinic. Either Shinsa named a
   * 治療院 RAH does not have (レセプト特定不可 / clinic_not_found — the commonest row in that inbox), or the
   * row is about master data every site shares, or it is a 状態の不一致 row covering a whole site at once.
   * Those rows appear on EVERY site's screen — see the backend's scopeToSites().
   */
  clinic_id: number | null;
  /** 治療院ID — clinics.clinic_number, joined at read time. */
  clinic_number: string | null;
  /** 治療院名 — clinic_informations.name, latest version. */
  clinic_name: string | null;
  /** サイト区分 — clinics.site_type. Also the column the list is filtered on. */
  site_type: string | null;
}

/** BD 10.0 — what the signed-in admin is allowed to see, decided by the server, not by the URL. */
export interface SyncNotificationSiteScope {
  /**
   * Whether サイト区分 is worth a column. False on a single-site portal, where every row would repeat the
   * same value; true for a super-admin or a parent tenant, who are looking at more than one site at once.
   */
  shows_site_column: boolean;
  /** The site_types in scope; null = every site (super-admin on the master portal). */
  site_types: string[] | null;
}

/**
 * BD 13.0 — the derived sync outcome, TWO values, read off `status` alone:
 *   recovered (同期成功) = status resolved
 *   pending   (未同期)   = everything else (unread / read)
 *
 * There is no 同期失敗. 「it failed again」 is a COUNT, and it is shown as one in 試行回数 — the old third
 * state was derived from `attempts`, which outside the 同期失敗 inbox counts daily reconcile runs rather than
 * send attempts, so it labelled month-long drift as a send failure that never happened.
 */
export type SyncNotificationSyncState = 'pending' | 'recovered';

/**
 * Which inbox the 同期通知 screen is showing.
 *  sync_failed     — a push ATTEMPT failed; 再同期 is the right thing to try.
 *  reconcile_drift — RS-11's daily run found RAH and Shinsa disagreeing AFTER a push Shinsa answered 200.
 *                    Re-pushing cannot fix it, so those rows carry no action.
 */
export const SYNC_NOTIFICATION_CATEGORY = {
  SYNC_FAILED: 'sync_failed',
  RECONCILE_DRIFT: 'reconcile_drift',
  /**
   * A message FROM Shinsa that RAH could not tie to a rezept. The delivery is already consumed, so a resend at
   * the same version is skipped — the only fix is redoing the action on the Shinsa screen so it dispatches at a
   * higher version. Hence no 再同期 on these rows either.
   */
  SYNC_UNRESOLVED: 'sync_unresolved',
  /**
   * 返戻取消依頼 that 審査 answered with 「we hold no such rezept」. The push succeeded, so nothing retries by
   * itself; someone has to make the row findable on the Shinsa side, then 再同期.
   */
  RETURN_CANCEL_NOT_FOUND: 'return_cancel_not_found',
  /**
   * A 返戻 that arrived during the 14-day 返戻取消 shield: the badge was recorded, the rezept was left at 確定.
   * Expires on its own, so these rows are informational — no 再同期.
   */
  RETURN_BLOCKED: 'return_blocked',
  /**
   * A 管理者 was granted or refused access to 審査 / 開示. Informational: the change is already
   * applied, so these rows carry no action — 再同期 would re-send a grant that already landed. The
   * DELIVERY failing is a different row, in 同期失敗.
   */
  ADMIN_ACCESS_CHANGED: 'admin_access_changed',
} as const;

export type SyncNotificationCategory =
  (typeof SYNC_NOTIFICATION_CATEGORY)[keyof typeof SYNC_NOTIFICATION_CATEGORY];

export interface SyncNotificationQuery {
  page?: number;
  perPage?: number;
  /** Filter by derived sync state; omit for all. */
  syncState?: SyncNotificationSyncState;
  /** Show only unread (未読) rows. */
  unreadOnly?: boolean;
  /** Inbox to show; omit = sync_failed (the server default). */
  category?: SyncNotificationCategory;
  /** 発生日時 lower bound, YYYY-MM-DD (inclusive). */
  from?: string;
  /** 発生日時 upper bound, YYYY-MM-DD (inclusive — the server covers the whole day). */
  to?: string;
}

export interface SyncNotificationListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}
